import { renderPopup } from "@/layers/utility/popup";
import type { LocationTable } from "@/locationTable/types";
import type { Plot } from "@/reducer/client-models";
import type { WeatherTable } from "@/weatherTableView/types";
import { t } from "@lingui/core/macro";
import type { TephigramSchemaOutput } from "@mm/metx-workbench.meteomatics.com";
import type { TabLocationMarker, TabLocationMarkerSource } from "./types";

export type TabLocationMarkerFeatureProperties = {
  popupHtml: string;
  tooltip: string;
};

type MarkerSourceAccumulator = TabLocationMarker & {
  sourceKeys: Set<string>;
};

type TabMarkerInputs = {
  locationTables: LocationTable[];
  plots: Plot[];
  tephigrams: TephigramSchemaOutput[];
  weatherTables: WeatherTable[];
};

const toolTypeLabels: Record<TabLocationMarkerSource["toolType"], () => string> = {
  plot: () => t`Plot`,
  weatherTable: () => t`Weather Table`,
  locationTable: () => t`Location Table`,
  tephigram: () => t`Tephigram`,
};

const sourceSortOrder: Record<TabLocationMarkerSource["toolType"], number> = {
  plot: 0,
  weatherTable: 1,
  locationTable: 2,
  tephigram: 3,
};

const LOCATION_MARKER_MERGE_PRECISION_DECIMALS = 5;
const LOCATION_MARKER_MERGE_SCALE = 10 ** LOCATION_MARKER_MERGE_PRECISION_DECIMALS;
export const LOCATION_MARKER_MERGE_TOLERANCE = 0.5 / LOCATION_MARKER_MERGE_SCALE;

function getMarkerCoordinateKey(lat: number, lon: number): string {
  return `${normalizeCoordinate(lat)}|${normalizeCoordinate(lon)}`;
}

function getSourceKey(source: TabLocationMarkerSource): string {
  return `${source.toolType}|${source.toolId}|${source.groupName ?? ""}`;
}

function normalizeCoordinate(value: number): number {
  return Math.round(value * LOCATION_MARKER_MERGE_SCALE) / LOCATION_MARKER_MERGE_SCALE;
}

function normalizeTitle(title?: string | null): string | null {
  const trimmed = title?.trim();
  return trimmed ? trimmed : null;
}

function sortSources(sources: TabLocationMarkerSource[]): TabLocationMarkerSource[] {
  return [...sources].sort((left, right) => {
    const typeDelta = sourceSortOrder[left.toolType] - sourceSortOrder[right.toolType];
    if (typeDelta !== 0) {
      return typeDelta;
    }
    if (left.toolId !== right.toolId) {
      return left.toolId - right.toolId;
    }
    return (left.groupName ?? "").localeCompare(right.groupName ?? "");
  });
}

function addLocationMarker(
  markersByCoordinate: Map<string, MarkerSourceAccumulator>,
  location: { lat: number; lon: number; label?: string | null },
  source: TabLocationMarkerSource,
): void {
  const { lat, lon, label } = location;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return;
  }

  const normalizedLat = normalizeCoordinate(lat);
  const normalizedLon = normalizeCoordinate(lon);

  const coordinateKey = getMarkerCoordinateKey(lat, lon);
  const sourceKey = getSourceKey(source);
  const existingMarker = markersByCoordinate.get(coordinateKey);

  if (existingMarker) {
    if (!existingMarker.sourceKeys.has(sourceKey)) {
      existingMarker.sources.push(source);
      existingMarker.sourceKeys.add(sourceKey);
    }
    if (!existingMarker.label && label) {
      existingMarker.label = label;
    }
    return;
  }

  markersByCoordinate.set(coordinateKey, {
    lat: normalizedLat,
    lon: normalizedLon,
    label,
    sources: [source],
    sourceKeys: new Set([sourceKey]),
  });
}

export function buildTabLocationMarkers({
  plots,
  weatherTables,
  tephigrams,
  locationTables,
}: TabMarkerInputs): TabLocationMarker[] {
  const markersByCoordinate = new Map<string, MarkerSourceAccumulator>();

  for (const plot of plots) {
    if (!plot.location) {
      continue;
    }
    addLocationMarker(
      markersByCoordinate,
      {
        lat: plot.location.lat,
        lon: plot.location.lng,
        label: plot.location_info?.label ?? null,
      },
      {
        toolType: "plot",
        toolId: plot.id,
        toolTitle: normalizeTitle(plot.title),
      },
    );
  }

  for (const weatherTable of weatherTables) {
    if (!weatherTable.location) {
      continue;
    }
    addLocationMarker(markersByCoordinate, weatherTable.location, {
      toolType: "weatherTable",
      toolId: weatherTable.id,
      toolTitle: normalizeTitle(weatherTable.title),
    });
  }

  for (const tephigram of tephigrams) {
    if (!tephigram.location) {
      continue;
    }
    addLocationMarker(
      markersByCoordinate,
      {
        lat: tephigram.location.lat,
        lon: tephigram.location.lng,
        label: tephigram.location_info?.label ?? null,
      },
      {
        toolType: "tephigram",
        toolId: tephigram.id,
        toolTitle: normalizeTitle(tephigram.title),
      },
    );
  }

  for (const locationTable of locationTables) {
    for (const group of locationTable.groups) {
      for (const location of group.locations) {
        addLocationMarker(markersByCoordinate, location, {
          toolType: "locationTable",
          toolId: locationTable.id,
          toolTitle: normalizeTitle(locationTable.title),
          groupName: normalizeTitle(group.name),
        });
      }
    }
  }

  return [...markersByCoordinate.values()]
    .map(({ sourceKeys: _sourceKeys, sources, ...marker }) => ({
      ...marker,
      sources: sortSources(sources),
    }))
    .sort((left, right) => {
      if (left.lat !== right.lat) {
        return left.lat - right.lat;
      }
      return left.lon - right.lon;
    });
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function formatSourceLabel(source: TabLocationMarkerSource): string {
  const baseLabel = `${toolTypeLabels[source.toolType]()}`;
  const titleLabel = source.toolTitle ? `: ${source.toolTitle}` : "";
  const groupLabel = source.groupName ? ` (${source.groupName})` : "";
  return `${baseLabel}${titleLabel}${groupLabel}`;
}

export function buildLocationMarkerTooltip(marker: TabLocationMarker): string {
  const header = marker.label ? `${marker.label}\n` : "";
  return `${header}${marker.sources.map(formatSourceLabel).join("\n")}`;
}

export function buildLocationMarkerPopupHtml(marker: TabLocationMarker): string {
  return renderPopup({
    title: escapeHtml(marker.label ?? t`Location`),
    rows: marker.sources.map((source) => [t`Source`, escapeHtml(formatSourceLabel(source))]),
  });
}

export function buildLocationMarkerFeatureCollection(
  markers: TabLocationMarker[],
): GeoJSON.FeatureCollection<GeoJSON.Point, TabLocationMarkerFeatureProperties> {
  return {
    type: "FeatureCollection",
    features: markers.map((marker) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [marker.lon, marker.lat],
      },
      properties: {
        popupHtml: buildLocationMarkerPopupHtml(marker),
        tooltip: buildLocationMarkerTooltip(marker),
      },
    })),
  };
}
